import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { CalendlyButton } from "@/components/ui/CalendlyButton";
import { SITE } from "@/lib/site";
import { industries, industryPages, demos, type Demo } from "@/lib/data";
import { Icon, industryIconMap } from "@/components/ui/Icons";
import { directoryHref } from "@/lib/construction/places";

const nav = [
  { label: "Demos", href: "/#demos" },
  { label: "Pricing", href: "/pricing" },
  { label: "Contact", href: "/contact" },
];

const resourcesMenu = [
  {
    label: "Blog",
    href: "/blog",
    description: "Guides on winning more work online.",
    icon: "Sparkle" as const,
  },
  {
    label: "Areas we cover",
    href: "/locations",
    description: "Local pages for the cities we work in.",
    icon: "Pin" as const,
  },
  {
    label: "Construction companies",
    href: directoryHref(),
    description: "UK directory of builders and trades by town.",
    icon: "Buildings" as const,
  },
  {
    label: "Get a website",
    href: "/get-website",
    description: "How a build works, start to launch.",
    icon: "Compass" as const,
  },
  {
    label: "FAQ",
    href: "/#faq",
    description: "Common questions, answered plainly.",
    icon: "Check" as const,
  },
];

const industryMenu = industries.filter((it) => Boolean(industryPages[it.slug]));

function getDemosForIndustry(slug: string): Demo[] {
  const page = industryPages[slug];
  if (!page) return [];
  if (page.demoSlugs && page.demoSlugs.length) {
    return demos.filter((d) => page.demoSlugs!.includes(d.slug) && !d.unlisted);
  }
  if (page.demoCategory) {
    return demos.filter((d) => d.category === page.demoCategory && !d.unlisted);
  }
  return [];
}

export function Header() {
  const [activeSlug, setActiveSlug] = useState<string>(industryMenu[0]?.slug ?? "");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const activeIndustry = industryMenu.find((it) => it.slug === activeSlug);

  // Transparent over the hero at the top of the page; solid once the user scrolls.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  const panelDemos = useMemo(() => {
    const matched = getDemosForIndustry(activeSlug);
    if (matched.length >= 4) return matched.slice(0, 4);
    // top up with newest demos to keep the panel balanced
    const fillers = [
      ...demos.filter((d) => d.isNew && !d.unlisted && !matched.some((m) => m.slug === d.slug)),
      ...demos.filter((d) => !d.isNew && !d.unlisted && !matched.some((m) => m.slug === d.slug)),
    ];
    return [...matched, ...fillers].slice(0, 4);
  }, [activeSlug]);

  return (
    <>
    <header
      className={`sticky top-0 z-40 border-b backdrop-blur-xl transition-colors duration-300 ${
        scrolled
          ? "border-ink/[0.06] bg-canvas/80"
          : "border-transparent bg-transparent"
      }`}
    >
      <Container>
        <div className="flex h-16 items-center justify-between gap-6 md:h-20">
          <Link href="/" className="group flex items-center gap-2.5" aria-label={SITE.brand}>
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-ink text-accent transition-transform duration-500 ease-smooth group-hover:rotate-[-6deg]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 21V9l9-5 9 5v12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <path d="M9 21v-7h6v7" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="hidden font-display text-[15px] font-bold tracking-tight text-ink sm:block">
              ConstructionWebDesign<span className="text-ink/40">.uk</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            <div className="group/industries relative">
              <Link
                href="/#industries"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink group-focus-within/industries:bg-ink/5 group-focus-within/industries:text-ink"
                aria-haspopup="true"
              >
                Industries
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  aria-hidden="true"
                  className="text-ink/40 transition-transform duration-300 group-hover/industries:rotate-180 group-focus-within/industries:rotate-180"
                >
                  <path d="M2 3.5L5 6.5l3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>

              <div
                className="invisible fixed left-1/2 top-16 z-50 w-[min(1080px,calc(100vw-2rem))] -translate-x-1/2 translate-y-1 pt-3 opacity-0 transition-all duration-200 ease-smooth group-hover/industries:visible group-hover/industries:translate-y-0 group-hover/industries:opacity-100 group-focus-within/industries:visible group-focus-within/industries:translate-y-0 group-focus-within/industries:opacity-100 md:top-20"
                role="menu"
              >
                <div className="flex max-h-[calc(100dvh-6rem)] flex-col overflow-hidden rounded-2xl border border-ink/[0.08] bg-white shadow-[0_24px_60px_-20px_rgba(11,17,32,0.25)] ring-1 ring-ink/[0.02]">
                  <div className="grid min-h-0 flex-1 grid-cols-5">
                    <div className="col-span-2 flex min-h-0 flex-col border-r border-ink/[0.06] bg-canvas/60 p-4 xl:p-6">
                      <div className="font-display text-base font-bold tracking-tight text-ink">
                        Industries
                      </div>
                      <p className="mt-1 text-xs leading-snug text-ink-muted">
                        Hover a trade to see matching demos.
                      </p>
                      <ul className="-mr-2 mt-4 min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-2">
                        {industryMenu.map((it) => {
                          const IconCmp = Icon[industryIconMap[it.slug] ?? "Hammer"];
                          const isActive = it.slug === activeSlug;
                          return (
                            <li key={it.slug}>
                              <Link
                                href={`/industries/${it.slug}`}
                                role="menuitem"
                                onMouseEnter={() => setActiveSlug(it.slug)}
                                onFocus={() => setActiveSlug(it.slug)}
                                className={`group/row flex items-center gap-3 rounded-[8px] px-2 py-2 text-sm transition-colors duration-200 focus:outline-none ${
                                  isActive
                                    ? "bg-white text-ink"
                                    : "text-ink hover:bg-white"
                                }`}
                              >
                                <span
                                  className={`grid h-7 w-7 shrink-0 place-items-center rounded-[6px] transition-colors duration-200 ${
                                    isActive ? "text-ink" : "text-ink-muted group-hover/row:text-ink"
                                  }`}
                                >
                                  <IconCmp width={16} height={16} />
                                </span>
                                <span className="font-medium">{it.name}</span>
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  aria-hidden="true"
                                  className={`ml-auto transition-all duration-200 ${
                                    isActive
                                      ? "translate-x-0.5 text-ink/60"
                                      : "text-ink/0 group-hover/row:translate-x-0.5 group-hover/row:text-ink/40"
                                  }`}
                                >
                                  <path d="M2.5 7h9m0 0L7.5 3m4 4l-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                </svg>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    <div className="col-span-3 min-h-0 overflow-y-auto p-4 xl:p-6">
                      <div className="flex items-baseline justify-between gap-3">
                        <div>
                          <div className="font-display text-base font-bold tracking-tight text-ink">
                            {activeIndustry ? `${activeIndustry.name} demos` : "Featured demos"}
                          </div>
                          <div className="mt-0.5 text-xs text-ink-muted">
                            {activeIndustry
                              ? `Templates we can spin up for ${activeIndustry.name.toLowerCase()}.`
                              : "Demo templates your website can start from."}
                          </div>
                        </div>
                        <Link
                          href="/#demos"
                          role="menuitem"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-ink-muted transition-colors hover:text-ink"
                        >
                          View all
                          <svg width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path d="M2.5 7h9m0 0L7.5 3m4 4l-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                          </svg>
                        </Link>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        {panelDemos.map((d) => (
                          <a
                            key={d.slug}
                            href={d.href}
                            role="menuitem"
                            className="group/card relative flex gap-3 overflow-hidden rounded-[10px] border border-ink/[0.08] bg-white p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-ink/20 hover:shadow-[0_12px_28px_-12px_rgba(11,17,32,0.18)] focus:outline-none focus:ring-2 focus:ring-ink/20"
                          >
                            <div className="relative h-[72px] w-[96px] shrink-0 overflow-hidden rounded-[4px] bg-ink/5">
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={`https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/${d.slug}.jpg`}
                                alt={`${d.name} demo`}
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-smooth group-hover/card:scale-105"
                              />
                              {d.isNew ? (
                                <span className="absolute left-1.5 top-1.5 rounded-full bg-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ink">
                                  New
                                </span>
                              ) : null}
                            </div>
                            <div className="min-w-0 flex-1 pr-4">
                              <div className="font-display text-sm font-bold tracking-tight text-ink">
                                {d.name}
                              </div>
                              <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-ink-muted/80">
                                {d.category}
                              </div>
                              <div className="mt-1 line-clamp-2 text-xs leading-snug text-ink-muted">
                                {d.tagline}
                              </div>
                            </div>
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 14 14"
                              fill="none"
                              aria-hidden="true"
                              className="absolute right-3 top-3 text-ink/30 transition-all duration-300 group-hover/card:-translate-y-0.5 group-hover/card:translate-x-0.5 group-hover/card:text-ink"
                            >
                              <path d="M4 10L10 4M10 4H5M10 4v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center justify-between gap-4 border-t border-ink/[0.06] bg-canvas px-5 py-4">
                    <div className="min-w-0">
                      <div className="font-display text-sm font-bold tracking-tight text-ink">
                        Don't see your trade?
                      </div>
                      <div className="text-xs text-ink-muted">
                        We've built sites for every corner of construction.
                      </div>
                    </div>
                    <Link
                      href="/contact"
                      role="menuitem"
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-accent transition-transform duration-300 hover:-translate-y-0.5"
                    >
                      Tell us what you do
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M2.5 7h9m0 0L7.5 3m4 4l-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {nav.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="rounded-full px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
              >
                {item.label}
              </Link>
            ))}

            <div className="group/resources relative">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink group-focus-within/resources:bg-ink/5 group-focus-within/resources:text-ink"
                aria-haspopup="true"
              >
                More
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  aria-hidden="true"
                  className="text-ink/40 transition-transform duration-300 group-hover/resources:rotate-180 group-focus-within/resources:rotate-180"
                >
                  <path d="M2 3.5L5 6.5l3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <div
                className="invisible absolute right-0 top-full z-50 w-[min(320px,calc(100vw-2rem))] translate-y-1 pt-3 opacity-0 transition-all duration-200 ease-smooth group-hover/resources:visible group-hover/resources:translate-y-0 group-hover/resources:opacity-100 group-focus-within/resources:visible group-focus-within/resources:translate-y-0 group-focus-within/resources:opacity-100"
                role="menu"
              >
                <div className="max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-2xl border border-ink/[0.08] bg-white p-2 shadow-[0_24px_60px_-20px_rgba(11,17,32,0.25)] ring-1 ring-ink/[0.02]">
                  {resourcesMenu.map((item) => {
                    const IconCmp = Icon[item.icon];
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        role="menuitem"
                        className="group/item flex gap-3 rounded-[10px] p-3 transition-colors duration-200 hover:bg-canvas focus:bg-canvas focus:outline-none"
                      >
                        <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-[6px] text-ink-muted transition-colors duration-200 group-hover/item:text-ink">
                          <IconCmp width={16} height={16} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-ink">{item.label}</span>
                          <span className="mt-0.5 block text-xs leading-snug text-ink-muted">
                            {item.description}
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <CalendlyButton size="md">Book a Call</CalendlyButton>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/10 bg-white text-ink transition-colors hover:bg-ink/5 lg:hidden"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </Container>
    </header>

    <div
      id="mobile-nav"
        className={`fixed inset-0 z-50 lg:hidden ${menuOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <div
          className={`absolute inset-0 bg-ink/40 backdrop-blur-sm transition-opacity duration-300 ${
            menuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMenuOpen(false)}
        />
        <aside
          role="dialog"
          aria-label="Site menu"
          aria-modal="true"
          className={`absolute right-0 top-0 flex h-full w-[min(360px,88vw)] flex-col bg-canvas shadow-[-20px_0_60px_-20px_rgba(11,17,32,0.35)] transition-transform duration-300 ease-smooth ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between border-b border-ink/[0.06] px-5 py-4">
            <span className="font-display text-[15px] font-bold tracking-tight text-ink">
              ConstructionWebDesign<span className="text-ink/40">.uk</span>
            </span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-5 py-6" aria-label="Mobile">
            <ul className="space-y-0.5">
              {nav.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="block rounded-[10px] px-3 py-2.5 text-base font-medium text-ink transition-colors hover:bg-ink/5"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <div className="px-3 text-[11px] font-semibold uppercase tracking-caps text-ink-muted">
                More
              </div>
              <ul className="mt-2 space-y-0.5">
                {resourcesMenu.map((item) => {
                  const IconCmp = Icon[item.icon];
                  return (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm text-ink transition-colors hover:bg-ink/5"
                      >
                        <span className="grid h-7 w-7 shrink-0 place-items-center text-ink-muted">
                          <IconCmp width={16} height={16} />
                        </span>
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mt-6">
              <div className="px-3 text-[11px] font-semibold uppercase tracking-caps text-ink-muted">
                Industries
              </div>
              <ul className="mt-2 space-y-0.5">
                {industryMenu.map((it) => {
                  const IconCmp = Icon[industryIconMap[it.slug] ?? "Hammer"];
                  return (
                    <li key={it.slug}>
                      <Link
                        href={`/industries/${it.slug}`}
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm text-ink transition-colors hover:bg-ink/5"
                      >
                        <span className="grid h-7 w-7 shrink-0 place-items-center text-ink-muted">
                          <IconCmp width={16} height={16} />
                        </span>
                        <span className="font-medium">{it.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>

          <div className="border-t border-ink/[0.06] px-5 py-5">
            <CalendlyButton
              size="md"
              onClick={() => setMenuOpen(false)}
              className="w-full justify-center"
            >
              Book a Call
            </CalendlyButton>
            <a
              href={`mailto:${SITE.email}`}
              className="mt-3 block text-center text-xs text-ink-muted hover:text-ink"
            >
              {SITE.email}
            </a>
          </div>
        </aside>
      </div>
    </>
  );
}
